import React, { useEffect, useState } from 'react';
import { FormGroup, Label, Input, Table, Button } from 'reactstrap';
import { getSelectableModuleTriggers } from '../../../store/ModuleTriggerAction';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../misc/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isValidCron } from 'cron-validator';
import { Trans, useTranslation } from 'react-i18next';
import { CoreSelectableTriggerModule, CoreTriggerType, CoreModuleTrigger } from '../../../models/CoreModels';
import StatusCode from '../../../util/StatusCode';
import ModalComponent from '../../misc/ModalComponent';
import { RequestState } from '../../../models/RequestState';
import { RootState } from '../../../store/store';


interface TriggerConfigurationProps {
    moduleTriggers: CoreModuleTrigger[]
    disableInput: boolean
    onUpdateTriggers: (triggers: CoreModuleTrigger[]) => void
    moduleId?: string
}

const TriggerType = {
    ADD: "ADD",
    REMOVE: "REMOVE"
}

const TriggerConfiguration = (props: TriggerConfigurationProps) => {

    const [trigger, setTrigger] = useState<CoreTriggerType>(CoreTriggerType.Undefined);
    const [triggerValue, setTriggerValue] = useState<string | undefined>(undefined);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [errorText, setErrorText] = useState<string>('');

    const dispatch = useDispatch();
    const { t } = useTranslation(['configuration', 'common']);


    const selectableTriggers = useSelector<RootState, RequestState<CoreSelectableTriggerModule[]>>(state => state.moduleTrigger.selectableTriggers);

    useEffect(() => {
        if (props.moduleId) {
            getSelectableModuleTriggers(props.moduleId)(dispatch);
        }
    }, [dispatch, props.moduleId]);

    const addTrigger = (type: CoreTriggerType) => {
        let triggerObject: CoreModuleTrigger = {
            triggerType: 0
        }
        if (type === CoreTriggerType.CronSchedule) {
            triggerObject = { triggerType: type, cronSchedule: triggerValue }
        }
        if (type === CoreTriggerType.RunAfterModule) {
            triggerObject = { triggerType: type, runAfterModuleId: triggerValue }
        }
        updateTrigger(TriggerType.ADD, triggerObject, type)
    }

    const deleteTrigger = (i: number) => {
        let moduleTriggers = [...props.moduleTriggers];
        moduleTriggers.splice(i, 1)
        props.onUpdateTriggers(moduleTriggers)
        setTriggerValue(undefined);
        setTrigger(CoreTriggerType.Undefined);
    }

    const updateTrigger = (trigger: string, triggerObject: CoreModuleTrigger, type?: CoreTriggerType) => {
        let moduleTriggers = [...props.moduleTriggers];
        if (type === CoreTriggerType.CronSchedule) {
            const isCronValid = (cronSchedule: string) => isValidCron(cronSchedule, { seconds: true, alias: true, allowBlankDay: true });
            if (props.moduleTriggers.some(t => t.cronSchedule === triggerObject.cronSchedule)) {
                setIsOpen(true)
                setErrorText(t('triggerConfiguration.cronAlreadyExists', { ns: 'configuration' }))
                return;
            } else if (triggerObject.cronSchedule !== undefined && !isCronValid(triggerObject.cronSchedule)) {
                setIsOpen(true)
                setErrorText(t('triggerConfiguration.invalidCron', { ns: 'configuration' }));               
                return;
            }
        } else if (type === CoreTriggerType.RunAfterModule && props.moduleTriggers.some(trigger => trigger.runAfterModuleId === triggerObject.runAfterModuleId)) {
            setIsOpen(true)
            setErrorText(t('triggerConfiguration.moduleTriggerAlreadyExists', { ns: 'configuration' }));
            return;
        }
        moduleTriggers.push(triggerObject)

        props.onUpdateTriggers(moduleTriggers)
        setTriggerValue(undefined);
        setTrigger(CoreTriggerType.Undefined);
    }

    const renderSelectableModules = () => {
        if (selectableTriggers === undefined || selectableTriggers.code === StatusCode.PENDING) {
            return <Loading />;
        } else if (selectableTriggers.code === StatusCode.COMPLETE) {
            return <select id="moduletrigger" value={triggerValue || ""} onChange={(e) => setTriggerValue(e.target.value)} className="form-control">
                <option disabled value="">{t('triggerConfiguration.chooseModule', { ns: 'configuration' })}</option>
                {selectableTriggers.data.map(module =>
                    <option value={module.id} key={module.id}>{module.title}</option>
                )}
            </select>
        } else {
            return t('triggerConfiguration.failedToFetchModules', { ns: 'configuration' });
        }
    }

    const renderModuleSelector = () => {
        if (trigger === CoreTriggerType.RunAfterModule) {
            return <FormGroup className="trigger_module">
                <Label for="moduletrigger">{t('triggerConfiguration.scheduleStartAfter', { ns: 'configuration' })}:</Label>
                {renderSelectableModules()}
                <Button color="primary" disabled={!triggerValue} onClick={() => addTrigger(CoreTriggerType.RunAfterModule)}>{t('triggerConfiguration.add', { ns: 'configuration' })}</Button>
            </FormGroup>;
        };
        return null;
    };

    const renderModuleTrigger = (trigger: CoreModuleTrigger, index: number) => {
        let title = trigger.runAfterModuleTitle;
        if (trigger.triggerType === CoreTriggerType.CronSchedule) {
            title = trigger.cronSchedule;
        } else if (!title) {
            if (trigger.triggerType === CoreTriggerType.RunAfterModule && selectableTriggers.code === StatusCode.COMPLETE) {
                title = selectableTriggers.data.find(m => m.id === trigger.runAfterModuleId)?.title;
            } else if (selectableTriggers.code === StatusCode.ERROR) {
                title = t('triggerConfiguration.failedToLoadModuleName', { ns: 'configuration' });
            } else {
                title = t('triggerConfiguration.loading', { ns: 'configuration' }) + '...';
            }
        };

        return (
            <tr key={index}>
                <td>{t(`triggerConfiguration.moduleTriggers.${trigger.triggerType}`, { ns: 'configuration' })}</td>
                <td>{title}</td>
                {!props.disableInput && <td onClick={() => deleteTrigger(index)}><FontAwesomeIcon icon="trash" title={t('triggerConfiguration.removeTriggerTitle', { ns: 'configuration' })} /></td>}
            </tr>
        );
    };

    const renderModuleTriggers = () => {
        if (props.moduleTriggers.length > 0) {
            return <Table>
                <tbody>
                    {props.moduleTriggers.map((t, i) => renderModuleTrigger(t, i))}
                </tbody>
            </Table>
        } else {
            return t('triggerConfiguration.triggersMissing', { ns: 'configuration' });
        }
    };
            
    return (
        <FormGroup className="trigger_configuration">
            <h5>{t('triggerConfiguration.triggersHeading', { ns: 'configuration' })}</h5>
            {renderModuleTriggers()}

            <FormGroup className="add_trigger">
                <Label for="trigger">{t('triggerConfiguration.addTriggerTitle', { ns: 'configuration' })}</Label>
                <select disabled={props.disableInput} onChange={e => {setTrigger(Number(e.target.value)); setTriggerValue(undefined);}} value={trigger} id="trigger" className="form-control">
                    <option disabled value={CoreTriggerType.Undefined}>{t('triggerConfiguration.chooseTriggerType', { ns: 'configuration' })}</option>
                    <option value={CoreTriggerType.CronSchedule}>{t('triggerConfiguration.cronTrigger', { ns: 'configuration' })}</option>
                    <option value={CoreTriggerType.RunAfterModule}>{t('triggerConfiguration.moduleBasedTrigger', { ns: 'configuration' })}</option>
                </select>
            </FormGroup>

            {trigger === CoreTriggerType.CronSchedule &&
                <FormGroup className="trigger_module">
                    <Label for="cronSchedule">{t('triggerConfiguration.cronExpression', { ns: 'configuration' })}</Label>
                    <Input type="text" name="cronSchedule" id="cronSchedule" placeholder={t('triggerConfiguration.cronExpressionPlaceholder', { ns: 'configuration' })} value={triggerValue || ''} onChange={(e) => setTriggerValue(e.target.value)} disabled={props.disableInput} />
                    <Trans
                        t={t}
                        i18nKey="triggerConfiguration.cronAdditionalInfo"
                        components={{ br: <br />, a: <a href='https://crontab.guru/' target='_blank' rel='noopener noreferrer'>x</a> }}
                    />
                    <Button color="primary" disabled={!triggerValue} onClick={() => addTrigger(CoreTriggerType.CronSchedule)}>{t('triggerConfiguration.add', { ns: 'configuration' })}</Button>
                </FormGroup>
            }

            {renderModuleSelector()}
            {isOpen &&
                <ModalComponent
                    header={t('triggerConfiguration.errorTrigger', { ns: 'configuration' })}
                    cancel={t('action.cancel', { ns: 'common' })}
                    isOpen={isOpen}
                    toggleModal={() => (setIsOpen(!isOpen), setErrorText(''))}
                >{errorText} </ModalComponent>}
        </FormGroup>
    );
};

export default TriggerConfiguration;
